import domtoimage from 'dom-to-image'
import { saveAs } from 'file-saver'

// The results graphic is authored at a fixed 640x640 design size. Capturing at
// an explicit, scaled size keeps the export deterministic regardless of the
// browser window width. Without explicit dimensions, dom-to-image infers the
// output size from the live layout, which on wide desktop windows produced
// distorted/"unstructured" downloads. Rendering at 2x also yields a crisp,
// higher-resolution image.
const GRAPHIC_SIZE = 640
const GRAPHIC_SCALE = 2

function waitForImages(node) {
  const images = Array.prototype.slice.call(node.querySelectorAll('img'))
  return Promise.all(
    images.map(function (img) {
      if (img.complete) return Promise.resolve()
      return new Promise(function (resolve) {
        img.onload = resolve
        img.onerror = resolve
      })
    })
  )
}

export function downloadBattleResult(username, battle) {
  const input = document.getElementById('divToPrint')
  if (!input) return Promise.resolve()

  const options = {
    width: GRAPHIC_SIZE * GRAPHIC_SCALE,
    height: GRAPHIC_SIZE * GRAPHIC_SCALE,
    cacheBust: true,
    style: {
      transform: 'scale(' + GRAPHIC_SCALE + ')',
      transformOrigin: 'top left',
      width: GRAPHIC_SIZE + 'px',
      height: GRAPHIC_SIZE + 'px'
    }
  }

  const fileName = username + '_' + battle.title + '_' + 'results.png'

  // dom-to-image's first render is frequently incomplete (blank/blotchy)
  // because the embedded artist photos and grain texture haven't finished
  // decoding yet. Wait for images, then render once to warm the cache and
  // discard the result, and save the second (complete) render.
  return waitForImages(input)
    .then(function () {
      return domtoimage.toBlob(input, options)
    })
    .then(function () {
      return domtoimage.toBlob(input, options)
    })
    .then(function (blob) {
      saveAs(blob, fileName)
    })
    .catch(function (error) {
      console.error('oops, something went wrong!', error)
    })
}

export function formatDate(createdAt) {
  const date = new Date(createdAt)
  const dateTimeFormat = new Intl.DateTimeFormat('en', { year: 'numeric', month: 'numeric', day: '2-digit' })
  const [{ value: month },,{ value: day },,{ value: year }] = dateTimeFormat.formatToParts(date)

  return `${month}.${day}.${year}`
}

function timeDifference(current, previous) {
  const milliSecondsPerMinute = 60 * 1000
  const milliSecondsPerHour = milliSecondsPerMinute * 60
  const milliSecondsPerDay = milliSecondsPerHour * 24
  const milliSecondsPerMonth = milliSecondsPerDay * 30
  const milliSecondsPerYear = milliSecondsPerDay * 365

  const elapsed = current - previous

  if (elapsed < milliSecondsPerMinute / 3) {
    return 'just now'
  }

  if (elapsed < milliSecondsPerMinute) {
    return 'less than 1 min ago'
  } else if (elapsed < milliSecondsPerHour) {
    return Math.round(elapsed / milliSecondsPerMinute) + 'min ago'
  } else if (elapsed < milliSecondsPerDay) {
    return Math.round(elapsed / milliSecondsPerHour) + 'h ago'
  } else if (elapsed < milliSecondsPerMonth) {
    return Math.round(elapsed / milliSecondsPerDay) + ' days ago'
  } else if (elapsed < milliSecondsPerYear) {
    return Math.round(elapsed / milliSecondsPerMonth) + ' mo ago'
  } else {
    return Math.round(elapsed / milliSecondsPerYear) + ' years ago'
  }
}

export function timeDifferenceForDate(date) {
  const now = new Date().getTime()
  const updated = new Date(date).getTime()
  return timeDifference(now, updated)
}
